import type { ComponentProps } from 'react'
import type { Address, Chain, Hash } from 'viem'

import { cn } from '@repo/ui'

import type { ChainId } from '../web3.types'

import { getChain } from '../utils/chain.utils'

type AnchorProps = Omit<ComponentProps<'a'>, 'href' | 'target' | 'rel'>

/**
 * Base props for all Etherscan link variants.
 *
 * Extends anchor props so Radix primitives (e.g. `TooltipTrigger asChild`)
 * can forward `ref` and merged event handlers onto the underlying `<a>`.
 */
interface EtherscanBaseProps extends AnchorProps {
  chainId: ChainId
}

/**
 * Etherscan link to an address.
 */
interface EtherscanAddressProps extends EtherscanBaseProps {
  address: Address
}

/**
 * Etherscan link to a transaction hash.
 */
interface EtherscanTxProps extends EtherscanBaseProps {
  tx: Hash
}

/**
 * Etherscan link to a block number or block hash (bigint).
 */
interface EtherscanBlockProps extends EtherscanBaseProps {
  block: bigint
}

/**
 * The type of link to create for the block explorer.
 * @property {string} address - The link is to an address.
 * @property {string} tx - The link is to a transaction hash.
 * @property {string} block - The link is to a block hash.
 */
type EtherscanLinkType = 'address' | 'tx' | 'block'

function getEtherscanLink(
  value: Address | Hash | bigint,
  type: EtherscanLinkType,
  chain: Chain
): string {
  const baseUrl = chain?.blockExplorers?.default?.url || 'https://etherscan.io'

  switch (type) {
    case 'address':
      return `${baseUrl}/address/${value}`
    case 'tx':
      return `${baseUrl}/tx/${value}`
    case 'block':
      return `${baseUrl}/block/${value}`
    default:
      throw new Error('Invalid Etherscan link type')
  }
}

/**
 * Union of all Etherscan link variants.
 */
type EtherscanLinkProps = EtherscanAddressProps | EtherscanTxProps | EtherscanBlockProps

export function EtherscanLink({ children, chainId, className, ...props }: EtherscanLinkProps) {
  const chain = getChain(chainId)
  const baseClassName = 'text-foreground transition-colors hover:underline'
  const mergedClassName = cn(baseClassName, className)

  if ('address' in props) {
    const { address, title, ...rest } = props
    return (
      <a
        {...rest}
        title={title ?? `View on Etherscan: ${address}`}
        target="_blank"
        rel="noopener noreferrer"
        className={mergedClassName}
        href={getEtherscanLink(address, 'address', chain)}
      >
        {children || 'View Address'}
      </a>
    )
  }

  if ('tx' in props) {
    const { tx, title, ...rest } = props
    return (
      <a
        {...rest}
        title={title ?? `View on Etherscan: ${tx}`}
        target="_blank"
        rel="noopener noreferrer"
        className={mergedClassName}
        href={getEtherscanLink(tx, 'tx', chain)}
      >
        {children || 'View Transaction'}
      </a>
    )
  }

  if ('block' in props) {
    const { block, title, ...rest } = props
    return (
      <a
        {...rest}
        title={title ?? `View on Etherscan: ${block}`}
        target="_blank"
        rel="noopener noreferrer"
        className={mergedClassName}
        href={getEtherscanLink(block, 'block', chain)}
      >
        {children || 'View Block'}
      </a>
    )
  }

  // Warn if no valid props were passed
  console.warn('EtherscanLink rendered without valid props (address, tx, or block).')
  return null
}
